import { Editor } from '@tiptap/react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { inputStyles } from '@tribeplatform/react-ui-kit/Input'

import { T } from '../../../../i18n/components/T.js'
import { useStaticIntl } from '../../../../i18n/hooks/useStaticIntl.js'
import { useI18n } from '../../../../i18n/providers/I18nProvider.js'
import { I18nShape } from '../../../../i18n/types.js'
import { ImageDescriptionType } from '../types.js'

const descriptionOptionsGenerator = ({ $t }: I18nShape) => [
  {
    value: ImageDescriptionType.Alt,
    label: $t({
      defaultMessage: 'ALT text',
      id: 'FigureOpts.AltText.Label',
    }),
  },
  {
    value: ImageDescriptionType.Caption,
    label: $t({
      defaultMessage: 'Image caption',
      id: 'FigureOpts.Caption.Label',
    }),
  },
  {
    value: ImageDescriptionType.Overlay,
    label: $t({
      defaultMessage: 'Overlay text',
      id: 'FigureOpts.Overlay.Label',
    }),
  },
]

export const ImageDescriptionForm = (props: { editor: Editor }) => {
  const { editor } = props
  const descriptOpts = useStaticIntl(descriptionOptionsGenerator)
  const currentDescriptionType =
    editor.getAttributes('async-image').descriptionType
  const currentOpt = descriptOpts.find(
    opt => opt.value === currentDescriptionType,
  )
  const { $t } = useI18n()
  return (
    <div
      className={twMerge(
        'overflow-auto flex items-center divide-x divide-line-subdued p-1',
      )}
    >
      <div className="flex flex-col relative isolate w-full">
        <div className="relative">
          <textarea
            value={editor.getAttributes('async-image').description}
            onChange={e => {
              editor.commands.setImageAttributes({
                description: e.target.value,
              })
            }}
            placeholder={$t({
              defaultMessage: 'Add image description...',
              id: 'FigureOpts.Description.Placeholder',
            })}
            className={twMerge(
              clsx(
                inputStyles({
                  size: 'compact',
                  invalid: false,
                  disabled: false,
                }),
                'block w-full rounded-md',
                'flex-1 block w-full text-xs placeholder:text-label-xs',
                'px-1 py-1 resize-y',
                'min-h-[64px]',
                'focus:ring-0 focus-within:ring-0',
              ),
            )}
          />
        </div>
        <div className="flex justify-start py-1 post-list-filter text-xs">
          <Dropdown>
            <Dropdown.ButtonMinimal className="flex space-s-1">
              <T
                id="FigureOpts.Description.DisplayAs"
                defaultMessage="Display as: {display_type}"
                values={{
                  display_type: (
                    <span className="ms-1 inline-flex items-center text-interactive hover:text-interactive-hovered">
                      <span className="font-medium">{currentOpt.label}</span>
                      <SvgIcon
                        className="w-4 h-4 relative top-px"
                        name="chevron-down"
                      />
                    </span>
                  ),
                }}
              />
            </Dropdown.ButtonMinimal>
            <Dropdown.Items>
              {descriptOpts.map(opt => (
                <Dropdown.Item
                  className="text-xs"
                  onClick={() =>
                    editor.commands.setImageAttributes({
                      descriptionType: opt.value,
                    })
                  }
                  key={opt.value}
                >
                  {opt.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Items>
          </Dropdown>
        </div>
      </div>
    </div>
  )
}
